import { TextField, Theme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { SelectableItem } from "../../../types/SelectableItem";

export interface NestedInputProps {
  resetInputState: (callback: () => void) => void;
  item: SelectableItem;
  customValue: string | null;
  onCustomValueChange?: (value: string) => void;
  onSelectedChange: () => void;
  isSelected: boolean;
  customStrings: { label: string; requiredError: string };
}

const boxStyles = (theme: Theme) => ({
  "&.MuiTextField-root": {
    borderRadius: "30px",
    backgroundColor: theme.palette.common.white,
    marginTop: "10px",
  },
  "&.MuiTextField-root *": {
    borderRadius: "30px",
  },
  "&.MuiTextField-root label": {
    borderRadius: "0",
  },
  "&.MuiTextField-root input": {
    minWidth: "100px",
    border: `2px solid ${theme.palette.primary.contrastText}`,
  },
});

export const NestedInput: React.FC<NestedInputProps> = ({
  item,
  customStrings,
  customValue,
  onCustomValueChange,
  onSelectedChange,
  isSelected,
  resetInputState,
}) => {
  const [customInputState, setCustomInputState] = useState({
    touched: false,
    dirty: false,
  });
  const updateRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (updateRef.current && isSelected) {
      updateRef.current.focus();
    } else if (updateRef.current != null && !isSelected) {
      updateRef.current.blur();
    }
  }, [updateRef, isSelected]);

  if (!item.isCustom || customValue === null || !onCustomValueChange) {
    return null;
  }

  const handleResetInputState = () => {
    setCustomInputState({
      touched: false,
      dirty: false,
    });
  };

  resetInputState(handleResetInputState);

  const isRequired =
    isSelected && customInputState.touched && customValue === "";

  return (
    <TextField
      inputRef={updateRef}
      label={customStrings.label}
      name="custom"
      variant="outlined"
      sx={boxStyles}
      value={customValue}
      onBlur={() => {
        setCustomInputState({
          ...customInputState,
          touched: true,
        });
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onChange={(e) => {
        setCustomInputState({
          ...customInputState,
          dirty: true,
        });
        const newValue = e.target.value;
        !isSelected && newValue !== "" && onSelectedChange();
        onCustomValueChange && onCustomValueChange(newValue);
      }}
      size="small"
      fullWidth
      error={isRequired}
      helperText={isRequired ? customStrings.requiredError : null}
      autoComplete="off"
    />
  );
};
